import * as React from "react";
import type { Omittable } from "../../lib/props";

/**
 * Circular progress glyph: an outlined circle with a pie-slice fill. At 100%
 * the ring disappears and the icon renders as a single solid disc, with the
 * optional terminal-state glyph (check / x) knocked out of the disc so it
 * stays readable in any disc color. At exactly 0% the ring can render as
 * evenly spaced round dashes (`emptyRing="dashed"`) for a pending state.
 * Entity-agnostic — the consumer resolves what the percentage, color, and
 * overlay mean (e.g. a workflow stage's progress and done/canceled types).
 * `color` must be a concrete paintable value (resolved token or CSS string):
 * SVG presentation attributes cannot read var() indirection reliably across
 * the places this renders (inline glyphs, canvas-exported charts).
 */

// Overlay icon paths, authored on a 24x24 viewBox. Chunky solid paths on
// purpose — they read at 12–16px when knocked out of the disc.
const OVERLAY_PATHS = {
  check:
    "M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z",
  x: "M24 20.188l-8.315-8.209 8.2-8.282-3.697-3.697-8.212 8.318-8.31-8.203-3.666 3.666 8.321 8.24-8.206 8.313 3.666 3.666 8.237-8.318 8.285 8.203z",
} as const;

export type ProgressPieOverlay = keyof typeof OVERLAY_PATHS;

export function progressPiePath(
  cx: number,
  cy: number,
  r: number,
  percentage: number,
): string {
  if (percentage >= 100) return "";
  const angle = (Math.min(100, Math.max(0, percentage)) / 100) * 360;
  const rad = ((angle - 90) * Math.PI) / 180;
  const x = cx + r * Math.cos(rad);
  const y = cy + r * Math.sin(rad);
  const largeArc = angle > 180 ? 1 : 0;
  return `M${cx},${cy} L${cx},${cy - r} A${r},${r} 0 ${largeArc} 1 ${x},${y} Z`;
}

export type ProgressPieProps = Omittable<{
  /** Fill percentage, clamped to [0, 100]. */
  percentage: number;
  /** Concrete paintable color (resolved token rgb/hex or CSS color string). */
  color: string;
  /** Edge length in px. */
  size: number;
  /** Terminal-state glyph knocked out of the solid disc (only at 100%). */
  overlay?: ProgressPieOverlay;
  /** Ring style at exactly 0% — "dashed" renders the pending state as evenly
   * spaced round dashes. Any progress (>0%) always uses the solid ring. */
  emptyRing?: "solid" | "dashed";
}>;

export function ProgressPie({
  percentage,
  color,
  size,
  overlay,
  emptyRing = "solid",
}: ProgressPieProps): React.ReactElement {
  const reactId = React.useId();
  const half = size / 2;
  const strokeW = 2;
  const gap = 1.5;
  const ringR = half - strokeW / 2;
  const pieR = ringR - strokeW / 2 - gap;
  const pct = Math.min(100, Math.max(0, percentage));

  // useId emits colons, which break `url(#…)` fragment references.
  const maskId = `progress-pie-${reactId.replace(/:/g, "")}`;

  // Glyph is cut out of the disc (mask), so it stays readable on any surface
  // and in any disc color — sized generously for legibility at small sizes.
  const overlaySize = size * 0.5;
  const overlayOffset = (size - overlaySize) / 2;
  const overlayTransform = `translate(${overlayOffset}, ${overlayOffset}) scale(${overlaySize / 24})`;
  const overlayPath = overlay ? OVERLAY_PATHS[overlay] : undefined;

  // Pending ring: 8 evenly spaced dashes with round caps. Round caps extend
  // each dash by strokeW (strokeW/2 per end), so the authored dash length is
  // short — the visual dash then reads roughly equal to its gap.
  const isDashed = emptyRing === "dashed" && pct === 0;
  const dashSegment = (2 * Math.PI * ringR) / 8;
  const dashLen = Math.max(0.5, dashSegment * 0.25);

  return (
    <svg
      width={size}
      height={size}
      // The pixel size is authoritative: a host that pins the svgs it cannot
      // see (Button's descendant glyph rule) sets a CLASS size, and a class
      // beats the width/height attributes but not this.
      style={{ width: size, height: size }}
      // A pie is a data glyph painted in its own color, not an icon drawn in
      // the host's ink: the 80% descendant-svg rule Button, Badge and Toggle
      // apply to icons would dim it against the same pie drawn bare beside
      // them. An explicit opacity class is those rules' opt-out.
      className="opacity-100"
      viewBox={`0 0 ${size} ${size}`}
      fill="none"
      aria-hidden
      data-slot="progress-pie"
    >
      {pct >= 100 ? (
        <>
          {overlayPath ? (
            <mask id={maskId} maskUnits="userSpaceOnUse">
              <circle cx={half} cy={half} r={half} fill="#fff" />
              <path d={overlayPath} fill="#000" transform={overlayTransform} />
            </mask>
          ) : null}
          <circle
            cx={half}
            cy={half}
            r={half}
            fill={color}
            mask={overlayPath ? `url(#${maskId})` : undefined}
          />
        </>
      ) : (
        <>
          <circle
            cx={half}
            cy={half}
            r={ringR}
            fill="none"
            stroke={color}
            strokeWidth={strokeW}
            strokeLinecap={isDashed ? "round" : undefined}
            strokeDasharray={
              isDashed ? `${dashLen} ${dashSegment - dashLen}` : undefined
            }
            strokeDashoffset={isDashed ? dashLen / 2 : undefined}
          />
          {pct > 0 ? (
            <path d={progressPiePath(half, half, pieR, pct)} fill={color} />
          ) : null}
        </>
      )}
    </svg>
  );
}
